import { STORE_PROVIDER } from "shared/constants";
import { parseOrderData } from "util/helpers";
import {
  NO_DEPARTMENT_LABEL,
  getOrderCustomerLastName,
  getOrderDepartments,
  getOrderReadyByLabel,
  getOrderReadyBySortValue,
  getOrderSearchableNames,
  getPrimaryDepartment,
  orderNumberSortKey,
} from "util/orderOrganizer";

/**
 * Case-insensitive partial match on order # and searchable names.
 */
export function orderMatchesSearch(order, query) {
  if (query == null || String(query).trim() === "") return true;
  const needle = String(query).trim().toLowerCase();
  if (String(order.name || "").toLowerCase().includes(needle)) return true;
  return getOrderSearchableNames(order).some((name) =>
    name.toLowerCase().includes(needle),
  );
}

function compareOrderNumberDesc(a, b) {
  return orderNumberSortKey(b) - orderNumberSortKey(a);
}

/**
 * Primary comparison; tie-breaker: order # descending.
 */
function sortWithTiebreaker(primaryCompare) {
  return (a, b) => {
    const c = primaryCompare(a, b);
    if (c !== 0) return c;
    return compareOrderNumberDesc(a, b);
  };
}

function isMissingReadyBy(order) {
  return getOrderReadyBySortValue(order) == null;
}

function isMissingLastName(order) {
  return !getOrderCustomerLastName(order);
}

function isMissingDepartment(order) {
  return getPrimaryDepartment(order) === NO_DEPARTMENT_LABEL;
}

/**
 * True when a delivery order's checkout fell back to Shopify's generic
 * "backup_rate" shipping rate (the real carrier rate lookup failed at
 * checkout) and no Grocerbox Delivery record was ever created for it —
 * i.e. no courier was actually booked.
 *
 * Do NOT broaden this to "any delivery order with no Delivery record":
 * legitimate nationwide carriers (UPS, FedEx, Canada Post) never get a
 * Grocerbox Delivery record either, so that condition would false-positive
 * on every one of those orders. `provider === "backup_rate"` is the
 * precise signal — real carriers always have their own provider code.
 */
export function isBackupRateDeliveryError(order, deliveries) {
  return (
    order.service?.type === "delivery" &&
    order.service?.provider === "backup_rate" &&
    !deliveries.entities[order.id]
  );
}

/**
 * missing* flags: when true, value sorts last regardless of asc/desc.
 */
export function sortOrdersList(
  list,
  sortKey,
  sortDir,
  organizerEnabled,
) {
  const dir = sortDir === "asc" ? 1 : -1;
  const mult = organizerEnabled ? dir : -1; // disabled: always order # desc

  if (!organizerEnabled) {
    return [...list].sort((a, b) => compareOrderNumberDesc(a, b));
  }

  if (sortKey === "order_number") {
    return [...list].sort((a, b) => {
      const va = orderNumberSortKey(a);
      const vb = orderNumberSortKey(b);
      if (va !== vb) return (va - vb) * mult;
      return compareOrderNumberDesc(a, b);
    });
  }

  if (sortKey === "last_name") {
    return [...list].sort(
      sortWithTiebreaker((a, b) => {
        const ma = isMissingLastName(a);
        const mb = isMissingLastName(b);
        if (ma && mb) return 0;
        if (ma) return 1;
        if (mb) return -1;
        const va = getOrderCustomerLastName(a).toLowerCase();
        const vb = getOrderCustomerLastName(b).toLowerCase();
        return va.localeCompare(vb) * mult;
      }),
    );
  }

  if (sortKey === "department") {
    return [...list].sort(
      sortWithTiebreaker((a, b) => {
        const ma = isMissingDepartment(a);
        const mb = isMissingDepartment(b);
        if (ma && mb) return 0;
        if (ma) return 1;
        if (mb) return -1;
        const va = getPrimaryDepartment(a).toLowerCase();
        const vb = getPrimaryDepartment(b).toLowerCase();
        return va.localeCompare(vb) * mult;
      }),
    );
  }

  if (sortKey === "ready_by") {
    return [...list].sort(
      sortWithTiebreaker((a, b) => {
        const ma = isMissingReadyBy(a);
        const mb = isMissingReadyBy(b);
        if (ma && mb) return 0;
        if (ma) return 1;
        if (mb) return -1;
        const va = getOrderReadyBySortValue(a);
        const vb = getOrderReadyBySortValue(b);
        return (va - vb) * mult;
      }),
    );
  }

  return [...list].sort((a, b) => compareOrderNumberDesc(a, b));
}

/**
 * Apply structural filters (not search). Pass `organizerEnabled` to apply gated filters.
 */
export function applyOrderFilters(orders, ctx) {
  const {
    selectedFilterDate,
    location,
    shippingMethod,
    selectedDepartments,
    selectedPickupTimes,
    deliveries,
    getLocationName,
    organizerEnabled,
  } = ctx;

  return orders.filter((order) => {
    if (selectedFilterDate) {
      const { year, month, day } = parseOrderData(order);
      if (!year || !month || !day) return false;
      const orderDate = new Date(year, month - 1, day);
      if (orderDate.toLocaleDateString() !== selectedFilterDate) return false;
    }

    if (location?.length) {
      const want = location[0];
      let address = "";
      if (order.fulfillment_order?.assigned_location) {
        address = order.fulfillment_order.assigned_location.address1;
      } else if (order.fulfillments?.[0]) {
        const loc = ctx.shopLocations?.find(
          (l) => l.id === order.fulfillments[0].location_id,
        );
        address = loc?.address1 || "";
      }
      if (getLocationName(address) !== want) return false;
    }

    if (shippingMethod?.length) {
      const value = shippingMethod[shippingMethod.length - 1];
      const delivery = deliveries.entities[order.id];
      let ok = false;
      if (value === "Pickup") ok = order.service?.type === "pickup";
      else if (value === "Store Delivery") {
        ok =
          order.service?.type === "delivery" &&
          delivery?.shipping_provider === STORE_PROVIDER;
      } else if (value === "Courier Delivery") {
        ok =
          order.service?.type === "delivery" &&
          delivery?.shipping_provider !== STORE_PROVIDER;
      }
      if (!ok) return false;
    }

    if (organizerEnabled && selectedDepartments?.length) {
      const want = new Set(selectedDepartments);
      const depts = getOrderDepartments(order);
      const hit = depts.some((d) => want.has(d));
      if (!hit) return false;
    }

    if (organizerEnabled && selectedPickupTimes?.length) {
      const lab = getOrderReadyByLabel(order);
      if (!lab || !selectedPickupTimes.includes(lab)) return false;
    }

    return true;
  });
}

/**
 * Full pipeline: filter -> search -> sort.
 */
export function buildOrderListResults(orders, ctx) {
  const filtered = applyOrderFilters(orders, ctx);
  const searched = filtered.filter((o) =>
    orderMatchesSearch(o, ctx.queryValue),
  );
  return sortOrdersList(
    searched,
    ctx.sortKey,
    ctx.sortDir,
    ctx.organizerEnabled,
  );
}
